import { useCallback, useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CreditPackageDto } from "@gaole/shared";
import { api, ApiError } from "../../lib/api";
import { rm, num } from "../../lib/format";
import { toast } from "../../stores/toast";
import { useAuth } from "../../stores/auth";
import { useBottomBarActions } from "../../hooks/useBottomBarActions";
import type { BottomBarAction } from "../../stores/shell-bottom-bar";
import { ImageUploadField } from "../../components/ImageUploadField";
import { Badge, Input, Toggle, useConfirm } from "../../components/ui";
import { DataTable, type Column } from "../../components/DataTable";
import { RightSidebarPanel } from "../../components/RightSidebarPanel";

const DEFAULT_COIN = "/coin.png";

interface PackageForm {
  name: string;
  credits: string;
  price: string;
  compareAtPrice: string;
  sortOrder: string;
  active: boolean;
  sku: string;
  imageUrl: string;
}

function emptyForm(): PackageForm {
  return { name: "", credits: "", price: "", compareAtPrice: "", sortOrder: "0", active: true, sku: "", imageUrl: "" };
}

function rowToForm(row: CreditPackageDto): PackageForm {
  return {
    name: row.name,
    credits: String(row.credits),
    price: String(row.price),
    compareAtPrice: row.compareAtPrice != null ? String(row.compareAtPrice) : "",
    sortOrder: String(row.sortOrder),
    active: row.active,
    sku: row.sku,
    imageUrl: row.imageUrl ?? "",
  };
}

function buildBody(form: PackageForm, isCreate: boolean): Record<string, unknown> {
  const credits = Number.parseFloat(form.credits);
  const price = Number.parseFloat(form.price);
  const body: Record<string, unknown> = {
    name: form.name.trim(),
    credits,
    price,
    sortOrder: Number.parseInt(form.sortOrder, 10) || 0,
    active: form.active,
    imageUrl: form.imageUrl.trim() || null,
  };
  if (form.compareAtPrice.trim()) {
    body.compareAtPrice = Number.parseFloat(form.compareAtPrice);
  } else if (!isCreate) {
    body.compareAtPrice = null;
  }
  if (isCreate && form.sku.trim()) {
    body.sku = form.sku.trim().toUpperCase();
  }
  return body;
}

export function CreditPackagesEditor() {
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const { confirm, dialog } = useConfirm();

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<CreditPackageDto | null>(null);
  const [form, setForm] = useState<PackageForm>(emptyForm());

  const canWrite = can("settings.write");

  const listQuery = useQuery({
    queryKey: ["credit-packages-manage"],
    queryFn: () => api<{ items: CreditPackageDto[] }>("/wallet/packages/manage"),
  });

  const packages = listQuery.data?.items ?? [];

  const saveMutation = useMutation({
    mutationFn: (payload: { id?: string; body: Record<string, unknown> }) => {
      if (payload.id) {
        return api(`/wallet/packages/${payload.id}`, { method: "PUT", body: payload.body });
      }
      return api("/wallet/packages", { method: "POST", body: payload.body });
    },
    onSuccess: () => {
      toast("success", editing ? "Credit package updated" : "Credit package created");
      closeEditor();
      void queryClient.invalidateQueries({ queryKey: ["credit-packages-manage"] });
      void queryClient.invalidateQueries({ queryKey: ["credit-packs"] });
    },
    onError: (err) => toast("error", err instanceof ApiError ? err.message : "Save failed"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      api<{ ok: boolean; deleted?: boolean; deactivated?: boolean }>(`/wallet/packages/${id}`, { method: "DELETE" }),
    onSuccess: (res: { ok: boolean; deleted?: boolean; deactivated?: boolean }) => {
      toast("success", res.deactivated ? "Package deactivated" : "Package deleted");
      closeEditor();
      void queryClient.invalidateQueries({ queryKey: ["credit-packages-manage"] });
      void queryClient.invalidateQueries({ queryKey: ["credit-packs"] });
    },
    onError: (err) => toast("error", err instanceof ApiError ? err.message : "Delete failed"),
  });

  function closeEditor() {
    setEditorOpen(false);
    setEditing(null);
    setForm(emptyForm());
  }

  function openCreate() {
    setEditing(null);
    setForm(emptyForm());
    setEditorOpen(true);
  }

  function openEdit(row: CreditPackageDto) {
    setEditing(row);
    setForm(rowToForm(row));
    setEditorOpen(true);
  }

  function confirmDelete(row: CreditPackageDto) {
    confirm({
      title: row.active ? "Remove package?" : "Delete package?",
      message: row.active
        ? "This deactivates the package if it has order history, otherwise deletes it."
        : "Permanently remove this inactive package?",
      confirmLabel: row.active ? "Remove" : "Delete",
      danger: true,
      action: async () => {
        await deleteMutation.mutateAsync(row.id);
      },
    });
  }

  function submitPackage() {
    saveMutation.mutate({
      id: editing?.id,
      body: buildBody(form, !editing),
    });
  }

  const formValid =
    form.name.trim().length >= 2 &&
    form.credits.trim() !== "" &&
    !Number.isNaN(Number.parseFloat(form.credits)) &&
    form.price.trim() !== "" &&
    !Number.isNaN(Number.parseFloat(form.price));

  useBottomBarActions(() => {
    if (!canWrite) return [];
    if (editorOpen) {
      const actions: BottomBarAction[] = [
        { id: "cancel", label: "Cancel", onClick: closeEditor },
        {
          id: "save",
          label: editing ? "Save" : "Create",
          variant: "primary",
          disabled: saveMutation.isPending || !formValid,
          onClick: submitPackage,
        },
      ];
      if (editing) {
        actions.unshift({
          id: "delete",
          label: "Delete",
          variant: "danger",
          disabled: deleteMutation.isPending,
          onClick: () => confirmDelete(editing),
        });
      }
      return actions;
    }
    return [{ id: "create", label: "Create", icon: Plus, variant: "primary" as const, onClick: openCreate }];
  });

  const setPackageImage = useCallback(
    (urls: string[]) => setForm((f) => ({ ...f, imageUrl: urls[0] ?? "" })),
    [],
  );

  const columns: Column<CreditPackageDto>[] = useMemo(
    () => [
      {
        key: "image",
        header: "Coin",
        render: (r) => (
          <img
            src={r.imageUrl ?? DEFAULT_COIN}
            alt=""
            className="h-10 w-10 rounded-lg border border-line bg-input-bg object-contain p-1"
          />
        ),
      },
      {
        key: "name",
        header: "Name",
        sortValue: (r) => r.name,
        render: (r) => (
          <div>
            <p className="font-medium">{r.name}</p>
            <p className="font-mono text-[10px] text-muted-2">{r.sku}</p>
          </div>
        ),
      },
      {
        key: "credits",
        header: "Credits",
        sortValue: (r) => r.credits,
        render: (r) => <span className="tabular-nums">{num(r.credits)}</span>,
      },
      {
        key: "price",
        header: "Sale price",
        sortValue: (r) => r.price,
        render: (r) => rm(r.price),
      },
      {
        key: "compareAt",
        header: "Compare-at",
        sortValue: (r) => r.compareAtPrice ?? 0,
        render: (r) => (r.compareAtPrice != null ? rm(r.compareAtPrice) : "—"),
      },
      {
        key: "sort",
        header: "Sort",
        sortValue: (r) => r.sortOrder,
        render: (r) => r.sortOrder,
      },
      {
        key: "active",
        header: "Active",
        render: (r) => <Badge color={r.active ? "green" : "gray"}>{r.active ? "Yes" : "No"}</Badge>,
      },
    ],
    [],
  );

  const rowActions = canWrite
    ? {
        onEdit: openEdit,
        onDelete: confirmDelete,
      }
    : undefined;

  return (
    <>
      {dialog}
      <DataTable
        columns={columns}
        rows={packages}
        rowKey={(r) => r.id}
        loading={listQuery.isLoading}
        exportFilename="credit-packages"
        entityLabel="Credit package"
        onRowClick={canWrite ? openEdit : undefined}
        rowActions={rowActions}
        empty={{ title: "No credit packages", hint: canWrite ? "Create a top-up package for the Wallet page." : "No packages configured yet." }}
      />

      <RightSidebarPanel open={editorOpen} onClose={closeEditor} title={editing ? "Edit credit package" : "Create credit package"}>
        <div className="sidebar-form">
          <ImageUploadField
            label="Coin image"
            hint="Shown on this package in Wallet and checkout. PNG with transparency recommended."
            value={form.imageUrl ? [form.imageUrl] : []}
            onChange={setPackageImage}
            max={1}
          />
          <Input
            label="Name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            hint="Shown on the Wallet top-up grid"
          />
          <Input
            label="Credits granted"
            type="number"
            min="0"
            step="1"
            value={form.credits}
            onChange={(e) => setForm((f) => ({ ...f, credits: e.target.value }))}
          />
          <div className="form-row-2">
            <Input
              label="Sale price"
              type="number"
              min="0"
              step="0.01"
              value={form.price}
              onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
              hint="Charged at checkout"
            />
            <Input
              label="Compare-at price"
              type="number"
              min="0"
              step="0.01"
              value={form.compareAtPrice}
              onChange={(e) => setForm((f) => ({ ...f, compareAtPrice: e.target.value }))}
              hint="Optional, must exceed sale price"
            />
          </div>
          <Input
            label="Sort order"
            type="number"
            min="0"
            value={form.sortOrder}
            onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))}
            hint="Lower appears first on Wallet"
          />
          {editing ? (
            <Input label="SKU" value={form.sku} disabled hint="SKU is fixed after creation" />
          ) : (
            <Input
              label="SKU"
              value={form.sku}
              onChange={(e) => setForm((f) => ({ ...f, sku: e.target.value.toUpperCase() }))}
              hint="Optional — auto-generated from credits if empty"
            />
          )}
          <Toggle label="Active" checked={form.active} onChange={(v) => setForm((f) => ({ ...f, active: v }))} />
        </div>
      </RightSidebarPanel>
    </>
  );
}
